import React, { useEffect, useRef } from "react";
import cx from "classnames";
import { Form, Field } from "react-final-form";

import { useLocalization } from "../../../../../../context/LocalizeContextProvider";
import useCanvasAnatomi from "../../../../../../hooks/useCanvasAnatomi";
import { matchesGender } from "../../../../../../utils/anatomyMatch";

import InputField from "../../../../../components/InputField";
import AsyncSelect from "../../../../../components/AsyncSelect";
import UploadFileField from "../../../../../components/UploadFileField";
import { BrowserView, MobileView } from "react-device-detect";
import RadioField from "../../../../../components/RadioField";
import { Gender } from "../../../../../../utils/constants";
import MyAnnotation from "../../../../../components/MyAnnotation";
import CardWrapperInput from "../../../../../components/CardWrapperInput";
import PanelUpdatedData from "../../../../../components/PanelUpdatedData";
import scrollToFirstErrorOnSubmit from "../scrollToFirstErrorOnSubmit";

/**
 * Bridges canvas-derived `summaryAnnotation` into the RFF `examination` field
 * without shadowing user edits or server-persisted text.
 *
 * Rules:
 *   1. If the server persisted an examination string (hasServerValue), the
 *      canvas never overwrites — server wins.
 *   2. If the user has typed into the field (tracked via userEditedRef), the
 *      canvas never overwrites — user wins.
 *   3. Otherwise every new summaryAnnotation flows into the field, and
 *      lastCanvasValueRef records the last canvas-authored value so the
 *      Field's onChange can tell "user typed" from "we just wrote this".
 */
function CanvasExaminationSync({
  form,
  summaryAnnotation,
  hasServerValue,
  userEditedRef,
  lastCanvasValueRef,
}) {
  useEffect(() => {
    if (hasServerValue) return;
    if (userEditedRef.current) return;
    if ((summaryAnnotation || "") === (lastCanvasValueRef.current || "")) return;
    form.change("examination", summaryAnnotation || "");
    lastCanvasValueRef.current = summaryAnnotation || "";
  }, [form, summaryAnnotation, hasServerValue, userEditedRef, lastCanvasValueRef]);
  return null;
}

export default function DoctorAssesment({
  RenderActionButton,

  handleSubmitForm = () => undefined,
  initialValues = {},

  isDoctor = false,
  isNurse = false,
  isView = false,

  handleOnChange = () => undefined,
  loadOptions = () => undefined,

  annotationJson = [],
  patientGender = null,

  onEditClick = () => undefined,
}) {
  const localize = useLocalization();

  const {
    canvasRef,
    dataAnatomis,
    activeAnnotation,
    positionAnnotation,
    handleSetPosition,
    setSelectedImage,
    handleAddPosition,
    setClickLayer,
    clickLayer,
    activeType,
    selectedImage,
    handleChangeType,
    handleUndo,
    setPossitionAnnotation,
    summaryAnnotation,
  } = useCanvasAnatomi(patientGender);

  // Refs used by <CanvasExaminationSync> and the examination Field's onChange
  // to arbitrate between canvas-driven auto-fill and user typing.
  const userEditedRef = useRef(false);
  const lastCanvasValueRef = useRef("");
  const hasServerValue = Boolean(initialValues.examination);

  useEffect(() => {
    if (annotationJson && annotationJson.length > 0) {
      setPossitionAnnotation(annotationJson);
    }
  }, [annotationJson, setPossitionAnnotation]);

  return (
    <>
      <PanelUpdatedData
        updatedAt={initialValues?.updatedAt}
        updatedBy={initialValues?.updatedBy}
        isEdit={isView}
        onClickEdit={onEditClick}
      />
      <Form
        onSubmit={(value) => {
          const formDataWithAnnotation = {
            ...value,
            annotationDTOs: positionAnnotation,
            examination: value.examination || summaryAnnotation,
          };
          handleSubmitForm(formDataWithAnnotation);
        }}
        initialValues={initialValues}
        // Preserve user-edited (dirty) fields when the parent hands us a new
        // `initialValues` object identity — otherwise cancelling the leave-
        // confirmation modal (Batal) re-renders the parent and RFF's reinit
        // path wipes any field that only lives in RFF internal state
        // (anamnesia, examination, diagnosis, treatment, fileUploads).
        // See skill: unsaved-changes-guard — "The RFF reinit trap".
        keepDirtyOnReinitialize
        validate={(values) => {
          const errors = {};
          if (isDoctor) {
            if (!values.anamnesia) {
              errors.anamnesia = localize.getText(
                "generalRequiredFieldValidation",
                { fieldName: localize.getText("anamnesia") }
              );
            }
            if (!values.examination && !summaryAnnotation) {
              errors.examination = localize.getText(
                "generalRequiredFieldValidation",
                { fieldName: localize.getText("examination") }
              );
            }
            if (!values.treatment) {
              errors.treatment = localize.getText(
                "generalRequiredFieldValidation",
                { fieldName: localize.getText("treatment") }
              );
            }
          }
          return errors;
        }}
      >
        {({ handleSubmit, values, form }) => (
          <form onSubmit={scrollToFirstErrorOnSubmit(handleSubmit, form)}>
            <CanvasExaminationSync
              form={form}
              summaryAnnotation={summaryAnnotation}
              hasServerValue={hasServerValue}
              userEditedRef={userEditedRef}
              lastCanvasValueRef={lastCanvasValueRef}
            />
            <div className="d-flex gap-5 flex-wrap">
              <div className="flex-1 d-flex flex-column gap-3">
                {initialValues?.satuSehatEncounterId && (
                  <InputField
                    label={
                      localize.getText("satuSehat") +
                      " - " +
                      localize.getText("encounterId")
                    }
                    disabled={true}
                    value={initialValues?.satuSehatEncounterId}
                  />
                )}
                <Field name="allergies">
                  {({ input, meta }) => (
                    <CardWrapperInput
                      label={localize.getText("alergies")}
                      icon={"fas fa-exclamation-triangle"}
                      colorIcon={"#ED8030"}
                    >
                      <AsyncSelect
                        placeholder="Select Alergies..."
                        options={initialValues.allergies}
                        isMulti
                        isCreatable
                        error={meta.touched && meta.error}
                        onChange={(value) => {
                          input.onChange(value);
                          handleOnChange("allergies", value);
                        }}
                        onBlur={input.onBlur}
                        disabled={isView}
                        value={values.allergies || initialValues.allergies}
                      />
                    </CardWrapperInput>
                  )}
                </Field>
                <Field name="historyOfIllness">
                  {({ input, meta }) => (
                    <CardWrapperInput
                      label={localize.getText("historyOfIllness")}
                      icon={"fas fa-notes-medical"}
                      colorIcon={"#3B82F6"}
                    >
                      <InputField
                        type="textarea"
                        rows={6}
                        error={meta.touched && meta.error}
                        onChange={(value) => {
                          input.onChange(value);
                          handleOnChange("historyOfIllness", value);
                        }}
                        onBlur={input.onBlur}
                        value={
                          values.historyOfIllness ||
                          initialValues.historyOfIllness
                        }
                        disabled={isView}
                      />
                    </CardWrapperInput>
                  )}
                </Field>
                <Field name="familyHistoryOfDisease">
                  {({ input, meta }) => (
                    <CardWrapperInput
                      label={localize.getText("familyHistoryOfDisease")}
                      icon={"far fa-file"}
                      colorIcon={"#09BD3C"}
                    >
                      <InputField
                        type="textarea"
                        rows={6}
                        error={meta.touched && meta.error}
                        onChange={(value) => {
                          input.onChange(value);
                          handleOnChange("familyHistoryOfDisease", value);
                        }}
                        onBlur={input.onBlur}
                        value={
                          values.familyHistoryOfDisease ||
                          initialValues.familyHistoryOfDisease
                        }
                        disabled={isView}
                      />
                    </CardWrapperInput>
                  )}
                </Field>
                <Field name="routineMedications">
                  {({ input, meta }) => (
                    <CardWrapperInput
                      label={localize.getText("routineMedications")}
                      icon={"fas fa-briefcase-medical"}
                      colorIcon={"#D653C1"}
                    >
                      <InputField
                        type="textarea"
                        rows={6}
                        error={meta.touched && meta.error}
                        onChange={(value) => {
                          input.onChange(value);
                          handleOnChange("routineMedications", value);
                        }}
                        onBlur={input.onBlur}
                        value={
                          values.routineMedications ||
                          initialValues?.routineMedications
                        }
                        disabled={isView}
                      />
                    </CardWrapperInput>
                  )}
                </Field>
                {isDoctor && (
                  <>
                    <Field name="anamnesia">
                      {({ input, meta }) => (
                        <CardWrapperInput
                          label={`${localize.getText(
                            "subjective"
                          )} (${localize.getText("anamnesia")})`}
                          icon={"fas fa-stethoscope"}
                          colorIcon={"#FC2E53"}
                        >
                          <InputField
                            type="textarea"
                            rows={6}
                            id={input.name}
                            error={meta.touched && meta.error}
                            onChange={(value) => {
                              input.onChange(value);
                              handleOnChange("anamnesia", value);
                            }}
                            onBlur={input.onBlur}
                            value={
                              values.anamnesia ?? initialValues.anamnesia ?? ""
                            }
                            disabled={isView}
                          />
                        </CardWrapperInput>
                      )}
                    </Field>
                    <Field name="examination">
                      {({ input, meta }) => (
                        <CardWrapperInput
                          label={`${localize.getText(
                            "objective"
                          )} (${localize.getText("examination")})`}
                          icon={"fas fa-stethoscope"}
                          colorIcon={"#1D4ED8"}
                        >
                          <InputField
                            type="textarea"
                            rows={6}
                            id={input.name}
                            error={meta.touched && meta.error}
                            onChange={(value) => {
                              const next = value ?? "";
                              if (next === "") {
                                userEditedRef.current = false;
                              } else if (next !== lastCanvasValueRef.current) {
                                userEditedRef.current = true;
                              }
                              input.onChange(value);
                              handleOnChange("examination", value);
                            }}
                            onBlur={input.onBlur}
                            value={values.examination ?? ""}
                            disabled={isView}
                          />
                        </CardWrapperInput>
                      )}
                    </Field>
                    <Field name="diagnosis">
                      {({ input, meta }) => (
                        <CardWrapperInput
                          label={`${localize.getText(
                            "assessment"
                          )} (${localize.getText("diagnosis")})`}
                          icon={"fas fa-diagnoses"}
                          colorIcon={"#8B5CF6"}
                        >
                          <AsyncSelect
                            placeholder="Select Diagnosis..."
                            loadOptions={loadOptions}
                            isMulti
                            error={meta.touched && meta.error}
                            onChange={(value) => {
                              input.onChange(value);
                              handleOnChange("diagnosis", value);
                            }}
                            onBlur={input.onBlur}
                            disabled={isView}
                            value={values.diagnosis || initialValues.diagnosis}
                            isCreatable
                            isAsync
                          />
                        </CardWrapperInput>
                      )}
                    </Field>
                  </>
                )}

                <Field name="treatment">
                  {({ input, meta }) => (
                    <CardWrapperInput
                      label={`${localize.getText("plan")} (${localize.getText(
                        "treatment"
                      )})`}
                      icon={"fas fa-pills"}
                      colorIcon={"#10B981"}
                    >
                      <InputField
                        type="textarea"
                        rows={6}
                        id={input.name}
                        error={meta.touched && meta.error}
                        onChange={(value) => {
                          input.onChange(value);
                          handleOnChange("treatment", value);
                        }}
                        onBlur={input.onBlur}
                        value={values.treatment ?? initialValues.treatment ?? ""}
                        disabled={isView}
                      />
                    </CardWrapperInput>
                  )}
                </Field>

                {(isDoctor || isNurse) && (
                  <Field name="fileUploads">
                    {({ input, meta }) => (
                      <CardWrapperInput
                        label={localize.getText("fileUpload")}
                        icon={"fas fa-paperclip"}
                        colorIcon={"#6B7280"}
                      >
                        <UploadFileField
                          error={meta.touched && meta.error}
                          onChange={(value) => {
                            input.onChange(value);
                            handleOnChange("fileUploads", value);
                          }}
                          accept="file/*"
                          value={
                            values.fileUploads || initialValues?.fileUploads
                          }
                          disabled={isView}
                        />
                      </CardWrapperInput>
                    )}
                  </Field>
                )}

                <BrowserView>
                  <RenderActionButton />
                </BrowserView>
              </div>

              <div
                className={cx(
                  "text-center mobile-full-width",
                  !isDoctor && "d-none"
                )}
              >
                <div className="d-flex align-items-center justify-content-between w-100">
                  <RadioField
                    datas={Gender()}
                    value={activeType}
                    onChange={(value) => handleChangeType(value)}
                  />
                  {activeAnnotation &&
                    activeAnnotation?.annotationDetails?.length > 0 && (
                      <div
                        className="d-flex align-items-center gap-2 cursor-pointer"
                        onClick={handleUndo}
                      >
                        <i className="fas fa-undo"></i>
                        <p className="mb-0">{localize.getText("undo")}</p>
                      </div>
                    )}
                </div>
                <MyAnnotation
                  canvasRef={canvasRef}
                  positionAnnotation={activeAnnotation}
                  handleSetPosition={(e) => handleSetPosition(e)}
                  handleAddPosition={handleAddPosition}
                  setClickLayer={setClickLayer}
                  clickLayer={clickLayer}
                  isView={isView}
                />
                <div
                  className="d-flex mt-4 justify-content-between table-responsive"
                  style={{ maxWidth: "330px", overflowX: "scroll" }}
                >
                  {dataAnatomis
                    .filter((value) => matchesGender(value, activeType))
                    .map((item, index) => (
                      <img
                        src={item.imageUrl}
                        key={index}
                        alt=""
                        onClick={() => setSelectedImage(item.subType)}
                        style={{
                          width: 80,
                          height: 180,
                          objectFit: "cover",
                          cursor: "pointer",
                          border:
                            selectedImage === item.subType
                              ? "solid 1px #F0F0F0"
                              : "",
                        }}
                      />
                    ))}
                </div>
              </div>
            </div>
            <MobileView>
              <RenderActionButton />
            </MobileView>
          </form>
        )}
      </Form>
    </>
  );
}
